import { formatMonth, formatPlainMoney } from '../domain/format';
import type { MonthlySummary, StoredTreasuryState, TreasuryTransaction } from '../domain/types';

export function downloadText(filename: string, content: string, type = 'text/plain;charset=utf-8') {
  const blob = new Blob([content], { type });
  downloadBlob(filename, blob);
}

export function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function transactionsToCsv(transactions: TreasuryTransaction[]) {
  const header = [
    'Date',
    'Type',
    'Account',
    'Amount (GHS)',
    'Payment method',
    'Category',
    'Description',
    'Mission Fund expense',
    'Note'
  ];
  const rows = transactions.map((transaction) => [
    transaction.date,
    transaction.type === 'income' ? 'Money received' : 'Money spent',
    transaction.account === 'cash' ? 'Cash Account' : 'Bank Account',
    formatPlainMoney(transaction.amount),
    transaction.paymentMethod,
    transaction.category ?? '',
    transaction.description,
    transaction.missionFundExpense ? 'Yes' : 'No',
    transaction.note ?? ''
  ]);
  return toCsv([header, ...rows]);
}

export function monthlyReportToCsv(summary: MonthlySummary, showBalanceColumn: boolean, showNotesColumn: boolean) {
  return monthlyReportsToCsv([summary], showBalanceColumn, showNotesColumn);
}

export function monthlyReportsToCsv(summaries: MonthlySummary[], showBalanceColumn: boolean, showNotesColumn: boolean) {
  const header = ['Month', 'Account', 'Income (GHS)', 'Expense (GHS)'];
  if (showBalanceColumn) header.push('Balance (GHS)');
  if (showNotesColumn) header.push('Notes');

  const rows = summaries.flatMap((summary) =>
    summary.rows.map((row) => {
      const values = [formatMonth(summary.month), row.account, formatPlainMoney(row.income), formatPlainMoney(row.expense)];
      if (showBalanceColumn) values.push(row.showBalance ? formatPlainMoney(row.balance) : '');
      if (showNotesColumn) values.push(row.note);
      return values;
    })
  );

  return toCsv([header, ...rows]);
}

export function backupToJson(state: StoredTreasuryState) {
  return JSON.stringify(
    {
      exportedAt: new Date().toISOString(),
      app: 'sgrbc-treasury',
      version: 1,
      data: state
    },
    null,
    2
  );
}

function toCsv(rows: string[][]) {
  return rows.map((row) => row.map(escapeCsv).join(',')).join('\n');
}

function escapeCsv(value: string) {
  if (/[",\n]/.test(value)) return `"${value.replaceAll('"', '""')}"`;
  return value;
}
